import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Container, Card, Spinner, Alert, Button, Modal } from 'react-bootstrap';
import { API_BASE } from '../lib/apiBase';

const AdminJobApplications = () => {
  const role = sessionStorage.getItem('userRole');
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewer, setViewer] = useState({ show: false, url: '', filename: '', title: '' });

  const loadApplications = useCallback(async () => {
    if (!role) {
      setLoading(false);
      setApplications([]);
      return;
    }
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(`${API_BASE}/jobs/applications`, {
        headers: { 'x-user-role': role },
        withCredentials: true,
      });
      setApplications(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load job applications', err);
      setError('Unable to load applications. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [role]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  useEffect(() => () => {
    if (viewer.url) {
      window.URL.revokeObjectURL(viewer.url);
    }
  }, [viewer.url]);

  const closeViewer = () => {
    setViewer((prev) => {
      if (prev.url) window.URL.revokeObjectURL(prev.url);
      return { show: false, url: '', filename: '', title: '' };
    });
  };

  const openFile = async (application, type) => {
    if (!role) return;
    try {
      const id = application.id || application._id;
      const filename = type === 'resume'
        ? application.resume?.filename || 'resume'
        : application.coverLetter?.filename || 'cover-letter';
      const label = type === 'resume' ? 'Resume' : 'Cover Letter';

      const res = await axios.get(`${API_BASE}/jobs/applications/${id}/${type}`, {
        headers: { 'x-user-role': role },
        responseType: 'blob',
        withCredentials: true,
      });
      const contentType = res.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([res.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);

      if (contentType.includes('pdf')) {
        setViewer((prev) => {
          if (prev.url) window.URL.revokeObjectURL(prev.url);
          return {
            show: true,
            url,
            filename: filename || `${type}.pdf`,
            title: `${label} — ${application.fullName}`,
          };
        });
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.download = filename || `${type}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      }
    } catch (err) {
      console.error(`Failed to fetch ${type}`, err);
      alert('Unable to open file.');
    }
  };

  if (!role) {
    return (
      <Container className="py-5">
        <Alert variant="warning">Please log in as an admin or office user to view job applications.</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status" />
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Job Applications</h2>
          <div className="text-muted">{applications.length} submission{applications.length === 1 ? '' : 's'}</div>
        </div>
        <Button variant="outline-secondary" onClick={loadApplications}>
          Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {applications.length === 0 ? (
        <Card className="shadow-sm border-0">
          <Card.Body className="text-center text-muted py-5">
            No applications yet.
          </Card.Body>
        </Card>
      ) : (
        <div className="d-flex flex-column gap-3">
          {applications.map((app) => {
            const createdAt = app.createdAt ? new Date(app.createdAt) : null;
            const hasCoverLetter = Boolean(app.coverLetter && app.coverLetter.filename);
            return (
              <Card key={app.id || app._id} className="shadow-sm border-0">
                <Card.Body className="d-flex flex-column flex-md-row align-items-md-center gap-3">
                  <div className="flex-grow-1">
                    <h5 className="mb-1">{app.fullName}</h5>
                    <div className="text-muted small">
                      {app.jobTitle && <span className="me-2">Applied for: <strong>{app.jobTitle}</strong></span>}
                      {createdAt && <span>Submitted on {createdAt.toLocaleDateString()}</span>}
                    </div>
                    {app.email && (
                      <div className="text-muted small">Email: {app.email}</div>
                    )}
                    {app.phone && (
                      <div className="text-muted small">Phone: {app.phone}</div>
                    )}
                  </div>
                  <div className="d-flex gap-2 ms-md-auto align-items-center flex-wrap">
                    {hasCoverLetter ? (
                      <Button
                        variant="outline-primary"
                        onClick={() => openFile(app, 'cover-letter')}
                      >
                        View Cover Letter
                      </Button>
                    ) : (
                      <span className="text-muted small fst-italic">No cover letter attached</span>
                    )}
                    <Button
                      variant="primary"
                      onClick={() => openFile(app, 'resume')}
                    >
                      View Resume
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            );
          })}
        </div>
      )}

      <Modal
        show={viewer.show}
        onHide={closeViewer}
        size="xl"
        centered
        aria-labelledby="job-application-viewer-title"
        dialogClassName="pdf-viewer-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title id="job-application-viewer-title">{viewer.title || 'Document Preview'}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0 }}>
          {viewer.url ? (
            <iframe
              title={viewer.title || 'Document'}
              src={viewer.url}
              style={{ width: '100%', height: '80vh', border: 'none' }}
            />
          ) : (
            <div className="p-4">No document available.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeViewer}>
            Close
          </Button>
          {viewer.url && (
            <Button as="a" href={viewer.url} download={viewer.filename || 'document.pdf'} variant="primary">
              Download
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminJobApplications;
